/** Playback defaults for a new background set. */
export const SET_DEFAULTS = {
    enabled: true,
    interval: 6,
    transition: 'crossfade',
    transitionMs: 800,
    order: 'sequence',
};

export const ALIGN_KEYWORDS = [
    'center',
    'top',
    'bottom',
    'left',
    'right',
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right',
];

/** Keyword crops kept from the old shared focal point. */
export const ALIGN_POSITIONS = {
    center: 'center center',
    top: 'center top',
    bottom: 'center bottom',
    left: 'left center',
    right: 'right center',
    'top-left': 'left top',
    'top-right': 'right top',
    'bottom-left': 'left bottom',
    'bottom-right': 'right bottom',
};

/** Approximate source point for a keyword, used as the marker before a click. */
export const ALIGN_POINTS = {
    center: { x: 0.5, y: 0.5 },
    top: { x: 0.5, y: 0 },
    bottom: { x: 0.5, y: 1 },
    left: { x: 0, y: 0.5 },
    right: { x: 1, y: 0.5 },
    'top-left': { x: 0, y: 0 },
    'top-right': { x: 1, y: 0 },
    'bottom-left': { x: 0, y: 1 },
    'bottom-right': { x: 1, y: 1 },
};

export const RUNG_WIDTHS = [480, 768, 1280, 1920, 3000];

const TRANSITIONS = new Set(['crossfade', 'cut']);
const ORDERS = new Set(['sequence', 'shuffle']);

let setSeq = 0;

export function createSetId() {
    setSeq += 1;
    return `set_${Date.now().toString(36)}_${setSeq}`;
}

export function createSet(partial = {}) {
    return {
        id: partial.id || createSetId(),
        enabled: partial.enabled !== false,
        interval: SET_DEFAULTS.interval,
        transition: SET_DEFAULTS.transition,
        transitionMs: SET_DEFAULTS.transitionMs,
        order: SET_DEFAULTS.order,
        images: Array.isArray(partial.images) ? partial.images : [],
    };
}

function clamp01(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return Math.min(1, Math.max(0, n));
}

function clampInt(value, min, max, fallback) {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, Math.round(n)));
}

function clampSeconds(value, min, max, fallback) {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    const clamped = Math.min(max, Math.max(min, n));
    return Math.round(clamped * 100) / 100;
}

function readFocal(focal) {
    if (!focal || typeof focal !== 'object') return null;
    const x = clamp01(focal.x);
    const y = clamp01(focal.y);
    if (x == null || y == null) return null;
    return { x, y };
}

function readAlign(align) {
    return ALIGN_KEYWORDS.includes(align) ? align : null;
}

function cleanImage(raw, { alignFallback = null } = {}) {
    const id = typeof raw === 'string' ? raw : raw?.id;
    if (!id) return null;
    const focal = readFocal(typeof raw === 'object' ? raw.focal : null);
    const align = focal ? null : (readAlign(raw?.align) || alignFallback);
    const image = { id: String(id) };
    if (focal) image.focal = focal;
    if (align) image.align = align;
    return image;
}

function cleanSet(raw, index) {
    if (!raw || typeof raw !== 'object') return null;
    const images = (Array.isArray(raw.images) ? raw.images : [])
        .map((image) => cleanImage(image))
        .filter(Boolean);
    if (images.length === 0) return null;
    return {
        id: raw.id ? String(raw.id) : `set_${index + 1}`,
        enabled: raw.enabled !== false,
        interval: clampSeconds(raw.interval, 0.01, 120, SET_DEFAULTS.interval),
        transition: TRANSITIONS.has(raw.transition) ? raw.transition : SET_DEFAULTS.transition,
        transitionMs: clampInt(raw.transitionMs, 200, 3000, SET_DEFAULTS.transitionMs),
        order: ORDERS.has(raw.order) ? raw.order : SET_DEFAULTS.order,
        images,
    };
}

/**
 * Read path. A legacy `{ imageIds, focalPoint }` list becomes one enabled set.
 * Returns null when there is nothing to show.
 */
export function normalizeBackground(raw) {
    if (!raw || typeof raw !== 'object') return null;
    if (Array.isArray(raw.sets)) {
        const sets = raw.sets.map((set, index) => cleanSet(set, index)).filter(Boolean);
        return sets.length ? { sets } : null;
    }
    const ids = Array.isArray(raw.imageIds) ? raw.imageIds.filter(Boolean) : [];
    if (ids.length === 0) return null;
    const align = readAlign(raw.focalPoint) || 'center';
    return {
        sets: [{
            id: 'set_legacy',
            enabled: true,
            interval: SET_DEFAULTS.interval,
            transition: SET_DEFAULTS.transition,
            transitionMs: SET_DEFAULTS.transitionMs,
            order: SET_DEFAULTS.order,
            images: ids.map((id) => cleanImage(String(id), { alignFallback: align })).filter(Boolean),
        }],
    };
}

/**
 * Write path. Drops empty sets and illegal values. Null clears the field.
 */
export function sanitizeBackground(raw) {
    return normalizeBackground(raw);
}

export function eligibleSets(config, files = {}) {
    const norm = normalizeBackground(config);
    if (!norm) return [];
    return norm.sets.filter((set) => {
        if (set.enabled === false) return false;
        return set.images.some((image) => image.id && files?.[image.id]);
    });
}

export function hasBackground(config, files) {
    // Appearance play can force a disabled set — still treat that as a background.
    const forceId = config?.previewSetId;
    if (forceId) {
        const norm = normalizeBackground(config);
        if (norm?.sets?.some((set) => (
            set.id === forceId && set.images.some((image) => image.id && files?.[image.id])
        ))) {
            return true;
        }
    }
    return eligibleSets(config, files).length > 0;
}

export function pickSet(sets, random = Math.random) {
    const list = Array.isArray(sets) ? sets.filter(Boolean) : [];
    if (list.length === 0) return null;
    if (list.length === 1) return list[0];
    const roll = Number(random());
    const index = Math.min(list.length - 1, Math.max(0, Math.floor((Number.isFinite(roll) ? roll : 0) * list.length)));
    return list[index];
}

/**
 * Slides that exist in `files`, in stored order, or shuffled once for this call.
 * Pass a stable `random` if the order must survive a re-render.
 */
export function slidesFor(set, files = {}, random = Math.random) {
    const images = (set?.images || []).filter((image) => image?.id && files?.[image.id]);
    if (set?.order !== 'shuffle' || images.length < 2) return images;
    const copy = images.slice();
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const roll = Number(random());
        const j = Math.min(i, Math.max(0, Math.floor((Number.isFinite(roll) ? roll : 0) * (i + 1))));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

/**
 * The first id after `currentId` (wrapping) that `isReady` accepts, skipping
 * anything not loaded yet. Null when no other slide is ready.
 */
export function nextReadyId(order, currentId, isReady) {
    const ids = Array.isArray(order) ? order : [];
    if (!ids.length) return null;
    const from = ids.indexOf(currentId);
    for (let step = 1; step <= ids.length; step += 1) {
        const id = ids[(from + step + ids.length) % ids.length];
        if (id !== currentId && isReady(id)) return id;
    }
    return null;
}

export function alignObjectPosition(align) {
    return ALIGN_POSITIONS[align] || ALIGN_POSITIONS.center;
}

/**
 * object-position that pins `focal` (0–1 on the source) to the center of a
 * cover crop, clamped so the frame never shows empty edges.
 * Without a measured box, returns the focal point as percentages.
 */
export function coverObjectPosition(focal, imageSize, boxSize) {
    const fx = clamp01(focal?.x) ?? 0.5;
    const fy = clamp01(focal?.y) ?? 0.5;
    const imageW = Number(imageSize?.width) || 0;
    const imageH = Number(imageSize?.height) || 0;
    const boxW = Number(boxSize?.width) || 0;
    const boxH = Number(boxSize?.height) || 0;
    if (!imageW || !imageH || !boxW || !boxH) {
        return `${fx * 100}% ${fy * 100}%`;
    }
    const scale = Math.max(boxW / imageW, boxH / imageH);
    const renderedW = imageW * scale;
    const renderedH = imageH * scale;
    const axis = (box, rendered, f) => {
        if (Math.abs(rendered - box) < 0.5) return 0.5;
        const raw = (box / 2 - f * rendered) / (box - rendered);
        return Math.min(1, Math.max(0, raw));
    };
    const x = axis(boxW, renderedW, fx);
    const y = axis(boxH, renderedH, fy);
    return `${x * 100}% ${y * 100}%`;
}

/**
 * coverObjectPosition worked out by CSS against the frame's container query
 * units, so it is exact before script has measured anything. The frame must
 * be a size container.
 */
export function coverPositionCss(focal, imageSize) {
    const fx = clamp01(focal?.x) ?? 0.5;
    const fy = clamp01(focal?.y) ?? 0.5;
    const imageW = Number(imageSize?.width) || 0;
    const imageH = Number(imageSize?.height) || 0;
    if (!imageW || !imageH) return `${fx * 100}% ${fy * 100}%`;
    const ratio = Math.round((imageW / imageH) * 10000) / 10000;
    const inverse = Math.round((imageH / imageW) * 10000) / 10000;
    const renderedW = `max(100cqw, 100cqh * ${ratio})`;
    const renderedH = `max(100cqh, 100cqw * ${inverse})`;
    const axis = (size, rendered, f) => `clamp(calc(100${size} - ${rendered}), calc(50${size} - ${f} * ${rendered}), 0px)`;
    return `${axis('cqw', renderedW, fx)} ${axis('cqh', renderedH, fy)}`;
}

/** Background position for a slide before the frame is measured. */
export function slidePositionCss(slide, imageSize) {
    if (slide?.focal && Number.isFinite(Number(slide.focal.x)) && Number.isFinite(Number(slide.focal.y))) {
        return coverPositionCss(slide.focal, imageSize);
    }
    return alignObjectPosition(slide?.align);
}

export function slideObjectPosition(slide, imageSize, boxSize) {
    if (slide?.focal && Number.isFinite(Number(slide.focal.x)) && Number.isFinite(Number(slide.focal.y))) {
        return coverObjectPosition(slide.focal, imageSize, boxSize);
    }
    return alignObjectPosition(slide?.align);
}

export function startRung() {
    return RUNG_WIDTHS[0];
}

/** Smallest generated width that covers the box at this pixel ratio, capped at 3000. */
export function viewportTarget(boxWidth, devicePixelRatio = 1) {
    const ratio = Number(devicePixelRatio) > 0 ? Number(devicePixelRatio) : 1;
    const need = Math.max(0, (Number(boxWidth) || 0) * ratio);
    const capped = Math.min(need, RUNG_WIDTHS[RUNG_WIDTHS.length - 1]);
    return RUNG_WIDTHS.find((width) => width >= capped) || RUNG_WIDTHS[RUNG_WIDTHS.length - 1];
}

/**
 * One step above the rung that merely covers the screen. A portfolio frame
 * looks soft at the exact match; Infinity means the original is sharper than
 * every generated file.
 */
export function qualityTarget(boxWidth, devicePixelRatio = 1) {
    const ratio = Number(devicePixelRatio) > 0 ? Number(devicePixelRatio) : 1;
    const need = Math.max(0, (Number(boxWidth) || 0) * ratio);
    const largest = RUNG_WIDTHS[RUNG_WIDTHS.length - 1];
    if (need > largest) return Number.POSITIVE_INFINITY;
    const match = RUNG_WIDTHS.find((width) => width >= need) || largest;
    return RUNG_WIDTHS.find((width) => width > match) || Number.POSITIVE_INFINITY;
}

export function nextRung(current) {
    const have = Number(current) || startRung();
    return RUNG_WIDTHS.find((width) => width > have) || RUNG_WIDTHS[RUNG_WIDTHS.length - 1];
}

/**
 * Same width choice as getBestAvailableUrl: a generated WebP at or under the
 * request, never the camera original while a WebP exists.
 */
function srcsetFor(file) {
    const srcset = file?.url?.srcset;
    if (srcset && Object.keys(srcset).length > 0) return srcset;
    const original = file?.url?.original;
    if (typeof original === 'string') {
        const base = original.replace(/-original\.[^.]+$/, '');
        if (base !== original) {
            return {
                480: `${base}-480.webp`,
                768: `${base}-768.webp`,
                1280: `${base}-1280.webp`,
                1920: `${base}-1920.webp`,
            };
        }
    }
    return {
        480: file?.webp?.sm || file?.url?.sm,
        768: file?.webp?.md || file?.url?.md,
        1280: file?.webp?.lg || file?.url?.lg,
        1920: file?.webp?.lg || file?.url?.lg,
    };
}

function numericWidths(srcset, originalWidth) {
    const keys = Object.keys(srcset || {})
        .map(Number)
        .filter((size) => Number.isFinite(size) && size > 0 && srcset[size]);
    const widths = (keys.length ? keys : RUNG_WIDTHS)
        .filter((size) => !originalWidth || size <= originalWidth);
    return [...new Set(widths)].sort((a, b) => a - b);
}

export function rungUrl(file, width) {
    const srcset = srcsetFor(file);
    const originalWidth = file?.dimensions?.width;
    const available = numericWidths(srcset, originalWidth);
    const filtered = {};
    for (const size of available) {
        if (srcset?.[size]) filtered[size] = srcset[size];
    }
    if (Object.keys(filtered).length === 0) return file?.url?.original || null;
    const requested = Number(width) || startRung();
    // Smallest-first lists must miss downward from the request, not from 480,
    // or a 1800px original never leaves the grid file.
    const smaller = available.filter((size) => size < requested).sort((a, b) => b - a);
    const sizesToTry = [requested, ...smaller];
    for (const size of sizesToTry) {
        if (filtered[size]) return filtered[size];
    }
    const sorted = [...available].sort((a, b) => b - a);
    for (const size of sorted) {
        if (filtered[size]) return filtered[size];
    }
    return file?.url?.original || null;
}

/**
 * A WebP one step above the screen match. A full-size WebP wins over the
 * camera file when the screen is sharper than 1920. With `allowOriginal` false
 * (a slow link) the largest WebP is the ceiling, never the camera file.
 */
export function qualityUrl(file, requestWidth, { allowOriginal = true } = {}) {
    const srcset = srcsetFor(file);
    const widths = numericWidths(srcset, file?.dimensions?.width);
    const finite = Number.isFinite(requestWidth);
    const request = finite ? requestWidth : Number.MAX_SAFE_INTEGER;
    const covering = widths.filter((size) => size >= request).sort((a, b) => a - b);
    if (covering.length && srcset?.[covering[0]]) return srcset[covering[0]];
    const largest = widths[widths.length - 1];
    const webp = largest ? srcset?.[largest] : null;
    const original = file?.url?.original;
    const originalWidth = Number(file?.dimensions?.width) || 0;
    if (allowOriginal && original && originalWidth > (largest || 0)) return original;
    return webp || original || null;
}

export function absoluteUrl(url) {
    if (!url || typeof url !== 'string') return '';
    if (url.startsWith('http') || url.startsWith('/')) return url;
    return `/${url}`;
}
